/**
 * rvm-engine.js — RobustVideoMatting (RVM) inference for video-bg-remover.
 *
 * RVM is a RECURRENT video matting network: each frame's inference consumes
 * the current frame PLUS four recurrent state tensors (r1i…r4i) and emits the
 * foreground (fgr), the alpha matte (pha) and updated state (r1o…r4o). Feeding
 * the state forward is exactly what removes the per-frame edge flicker that an
 * independent per-frame segmenter (ISNet/U2Net) produces — so frames MUST be
 * processed in strict temporal order and resetState() called per new clip.
 *
 * Runs on ONNX Runtime Web — WebGPU when available, WASM otherwise. The ORT
 * loader mirrors video-colorize-segment.js (CSP-safe: no JIT, vendored runtime).
 *
 * MODEL: only the MobileNetV3 backbone is used. The ResNet-50 export was
 * dropped — its graph has an AveragePool with ceil_mode that ONNX Runtime Web
 * cannot execute ("using ceil() in shape computation is not yet supported for
 * AveragePool"), so it crashes in the browser regardless of input size.
 *
 * ⚠️⚠️ "MobileNetV3 is RVM's own recommended real-time model and runs cleanly"
 * stood here until 2026-08-25 and was FALSE. The mobilenetv3 export carries the
 * SAME defect — three AveragePool nodes with ceil_mode=1 (scan the .onnx for the
 * protobuf bytes `ceil_mode` + 18 01; the fp32 export has 3, every other model
 * we ship has 0). It "ran cleanly" only because the machine that measured it had
 * no WebGPU *device*: when an EP is UNAVAILABLE, ORT drops it and silently uses
 * wasm — so the bug is invisible exactly where people test it. On a device that
 * really has WebGPU the throw lands in run(), not create(), and
 * `executionProviders:['webgpu','wasm']` is NOT a fallback for that: the session
 * was already accepted. Measured on prod Chrome + NVIDIA, a FRESH browser per
 * case: eps=[webgpu] → ceil() throw · eps=[webgpu,wasm] → ceil() throw ·
 * eps=[wasm] → 41 ms and correct output. 63 % of our sessions report a WebGPU
 * adapter, and /text-behind-image's video lane had 0 completions in 10 sessions
 * since launch. Hence verifySession() below: a session is not trusted until it
 * has actually RUN once, and one that cannot run is rebuilt on wasm.
 *
 * ⚠️ That fallback was the FIRST answer (26.08) and it only made the lane work,
 * not work WELL: wasm is single-threaded here, and matting is ~95 % of a video
 * export's per-frame time once a real WebCodecs decoder is in play. The second
 * answer, same day, is unlockWebGpuOps() — the three offending attributes are
 * edited out of the downloaded bytes, which is provably a no-op at input sizes
 * divisible by 32 (preprocess() now guarantees that) and hands the graph to the
 * GPU. Measured end to end on the real export, paired runs, same machine:
 * wasm 1.00 and 1.42 frames/s against WebGPU 2.30 and 2.37. The fallback stays
 * as the net underneath — belt AND braces, because the edit is only sound while
 * the precondition holds.
 *
 * Model I/O (standard RVM ONNX export):
 *   inputs : src [1,3,H,W] f32 RGB[0,1] CHW · r1i..r4i state · downsample_ratio [1]
 *   outputs: fgr [1,3,H,W] · pha [1,1,H,W] · r1o..r4o state
 *
 * Public API — window.VbrRvmEngine:
 *   ready(signal?)             → Promise<true>   (signal: AbortSignal — cancels the load)
 *   isReady()                  → bool
 *   getError()                 → null | Error
 *   resetState()               → void            (call before each sequential pass)
 *   matte(srcCanvas)           → Promise<canvas>  RGBA: RGB=fgr, A=pha; advances state
 *   matteStateless(srcCanvas)  → Promise<canvas>  one-off (resets state); for preview
 *   onModelProgress(fn)        → void            (fn(loadedBytes, totalBytes))
 */
(function (root) {
    'use strict';

    /* WHICH BUILD — asked of window.__ortBuild (device-capabilities.js), the
       one owner of that answer. Until 24.09.2026 this file named ort.min.js
       itself, three times: that is the JSEP build, and on WebKit it is
       onnxruntime#26827 (1 GB → 14 GB, the tab is killed). The owner hands
       WebKit the plain WASM build and every other engine the JSEP one; its
       fallback() is our own /cdn/jsdelivr/ proxy on the RU origin (a CDN is not
       reliably reachable from Russia) and jsDelivr elsewhere. The literal below
       is the fail-safe for a page without the owner — every tool page has it. */
    var ORT_FAILSAFE = '/vendor/onnxruntime/ort.min.js';
    var ORT_WASM_BASE = '/vendor/onnxruntime/';
    function ortOwner() {
        var ob = root.__ortBuild;
        return (ob && typeof ob.url === 'function') ? ob : null;
    }

    // onnxruntime-web dropped its non-SIMD wasm build in 1.19, so on a CPU whose
    // V8 refuses WASM SIMD (old pre-SSE4 silicon — a real tester's Win10 PC) the
    // modern runtime has NO backend at all and every file dies with the opaque
    // "no available backend found" (#6966). 1.18 is the last release that still
    // ships ort-wasm.wasm — self-hosted, loaded when SIMD is missing.
    // ?ortlegacy=1 forces the path so tests can prove it on normal hardware.
    //
    // ⚠️ The same 1.18 is also the lane for a device that cannot RESERVE the
    // modern build's memory (02.10.2026). Since 1.19 ORT ships only the threaded
    // wasm, and that binary imports a SHARED memory with a 4 GiB maximum (read
    // from the bytes: shared, 256…65536 pages — both the plain and the JSEP
    // build); a shared memory cannot move, so the engine must reserve all of it
    // up front, and WebKit on iOS ≤ 17 has a 2 GiB cage for it. Every RVM start
    // on iOS 17 died at init with «no available backend found. ERR: [wasm]
    // RangeError: Out of memory» (RU+intl, 18.09–02.10: 6 starts, 6 failures,
    // 0 results; onnxruntime#22086 is the upstream report). 1.18's
    // ort-wasm-simd.wasm owns an ordinary memory (not shared, it grows) — so
    // that failure steps down to it, with SIMD, and the device is remembered.
    var ORT_LEGACY = '/vendor/onnxruntime-legacy/ort.min.js';
    var ORT_LEGACY_WASM_BASE = '/vendor/onnxruntime-legacy/';
    var NOSHARE_KEY = 'rvm_noshare';
    var NOSHARE_DAYS = 30;
    function noShareRemembered() {
        try {
            var t = +localStorage.getItem(NOSHARE_KEY) || 0;
            return t > 0 && Date.now() - t < NOSHARE_DAYS * 864e5;
        } catch (_) { return false; }
    }
    function rememberNoShare() {
        try { localStorage.setItem(NOSHARE_KEY, String(Date.now())); } catch (_) {}
        // …and the owner is told (the failure's own text is in noShareNote), so every
        // other engine on this browser starts on 1.18 as well
        try { var own = root.__ortBuild; if (own && own.initFailed) own.initFailed(noShareNote, { legacy: '', from: 'rvm' }); } catch (_) {}
    }
    function isReservationFailure(e) {
        var m = String((e && e.message) || e || '');
        return /out of memory/i.test(m) && /RangeError|\[wasm\]/i.test(m);
    }
    var FORCE_LEGACY = false;
    try { FORCE_LEGACY = /[?&]ortlegacy=1/.test(root.location.search); } catch (_) {}
    var SIMD_OK = (function () {
        if (FORCE_LEGACY) return false;
        // the canonical feature-test module: (func (result v128) i32.const 0 i8x16.splat i8x16.popcnt)
        try {
            return WebAssembly.validate(new Uint8Array([
                0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3,
                2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
            ]));
        } catch (_) { return false; }
    })();

    // Origin-served only — GitHub release assets carry no CORS header, so there
    // is no browser-fetchable CDN mirror. download-all.sh places it on origin.
    var MODEL_URL = '/vendor/onnx/rvm_mobilenetv3_fp32.onnx';

    /* Both input dims are rounded to this in preprocess(); see the note there
       and unlockWebGpuOps() for why it is not 4.
     *
     * ⚠️ 64, not 32 (2026-09-02). With GRID 32 the edited graph still died on
     * REAL 720p footage: 1280×720 rounds to 1280×736, and 736 is an ODD multiple
     * of 32. Swept on WebGPU (rvm-sweep-scratch): at width 1280 every odd
     * multiple of 32 (544, 608, 672, 736, 800 … 1120) throws at Concat_199 for
     * k = 0.25, 0.375 and 0.5 alike, every even multiple passes; at widths 1216
     * and 1152 everything passes. A multiple of 64 on BOTH dims is the only rule
     * that held across all 189 cases, and it keeps every downsampled size an
     * integer for all three ratios (64·k ∈ {16, 24, 32}). The extra rounding is
     * at most 3 % of a dimension and the matte is scaled back afterwards. */
    var GRID = 64;

    /* Turn three `ceil_mode=1` pooling attributes into `ceil_mode=0`, in place,
       in the downloaded bytes.
     *
     *  WHY. ORT-web's WebGPU operator refuses ceil_mode on AveragePool outright
     *  ("using ceil() in shape computation is not yet supported"), so the whole
     *  graph fell back to single-threaded wasm — and the matting pass is ~95 %
     *  of a video export's per-frame time once a real WebCodecs decoder is in
     *  play (measured: extraction 1 ms/frame with a GPU, 154 ms without).
     *
     *  WHY IT IS SAFE. The three nodes are chained 2x2 pools, stride 2, no pad.
     *  ceil and floor differ only where a feature map has an odd dimension, and
     *  preprocess() now guarantees inputs divisible by 32, which makes every
     *  intermediate even. Verified, not argued: same input, same state, both
     *  graphs on wasm — outputs bit-identical (max |diff| = 0) at 64x64,
     *  256x256, 160x288, 544x960 and 1088x1920. At 1080x1920 and 540x960 (not
     *  divisible by 32) the edited graph does NOT silently drift — it throws at
     *  Concat, which is why the rounding is a precondition and not a nicety.
     *
     *  Measured payoff, same machine, sequential frames carrying recurrent
     *  state, 1088x1920 @ ratio 0.25: wasm 324 ms/frame → WebGPU 67 ms/frame.
     *
     *  An in-memory edit rather than a second .onnx on the origin: the file is
     *  15 MB, it is content-addressed in the SW cache, and a second copy would
     *  double the download for every visitor to buy nothing. verifySession()
     *  below still refuses to hand out a session that cannot actually run, so
     *  a future ORT that changes any of this degrades to wasm instead of
     *  breaking. */
    var CEIL_ON = [0x63,0x65,0x69,0x6c,0x5f,0x6d,0x6f,0x64,0x65,0x18,0x01];  // "ceil_mode" 18 01
    function unlockWebGpuOps(bytes) {
        var hits = 0, n = bytes.length - CEIL_ON.length;
        for (var i = 0; i <= n; i++) {
            if (bytes[i] !== CEIL_ON[0]) continue;
            var ok = true;
            for (var j = 1; j < CEIL_ON.length; j++) { if (bytes[i + j] !== CEIL_ON[j]) { ok = false; break; } }
            if (!ok) continue;
            bytes[i + CEIL_ON.length - 1] = 0x00;      // ceil_mode: 1 -> 0
            hits++;
            i += CEIL_ON.length - 1;
        }
        return hits;
    }

    var session = null;
    var usedProvider = null;          // what the verified session actually runs on
    var loadingPromise = null;
    var lastError = null;
    var ortLoadingPromise = null;
    var progressCb = null;
    var modelBytes = null;            // kept for a mid-run step-down to wasm
    var sessionGen = 0;               // bumped when a step-down rebuilds the runtime

    // Recurrent state — four tensors carried frame to frame. Seeded with a
    // [1,1,1,1] zero tensor; RVM resizes internally on the first frame.
    var rState = null;
    var ZERO = null;

    /* ── THE FAST PATH FIRST, and a way back from it (25.09.2026) ──────────
     *
     *  Two fast lanes, each with its own fallback:
     *
     *  GPU. WebGPU through the JSEP build wherever it runs. On WebKit that is
     *  asked for by name: `jsepOnDesktopWebKit` (Mac/iPad) and
     *  `jsepOnPhoneWebKit: 'rvm'` (iPhone). The phone lane is measured, not
     *  assumed — before 25.09 this engine ran there for three months at a
     *  median 302 ms/frame (n=20 matte passes; iPhone wasm 765, Android WebGPU
     *  793–1438) and reached review as often as Android (24 of 45). 25.09 took
     *  it away in the name of #26827 without a measurement, and «an iPhone
     *  never gets JSEP» became a 2.5× slowdown for everyone on iOS 26. What a
     *  page cannot catch — WebKit killing the process — the owner's ledger
     *  catches on the NEXT load (__ortBuild.phoneJsep: armed before the build
     *  loads, settled after SETTLE_FRAMES real frames); what it can catch
     *  (create/verify/run throwing) steps down at once to the PLAIN build,
     *  never to the wasm EP inside JSEP — JSEP without a GPU on WebKit is
     *  #26827 itself (+738 MB, measured 24.09).
     *
     *  ⚠️ 02.10.2026: the PHONE half of that is off again (PHONE_JSEP below),
     *  and this time by measurement. A week of the ledger on RU+intl
     *  (18.09–02.10): of ~70 iPhone runs that loaded the JSEP build, 23 left
     *  the page dead (`ort_jsep_phone_died`), every one on iOS 26 (Safari 26
     *  freezes its UA at «iPhone OS 18_7», hence 14 of them under 18.7); on
     *  the plain build ~10 % died. The cause is upstream and still open: WebKit's
     *  optimising compiler runs away on the huge JSEP functions — 400 % CPU,
     *  1–14 GB, reloads (onnxruntime#26827, WebKit 304810, Safari 26.2–26.6) —
     *  and the ledger only learns it from a run the person has already lost.
     *  An iPhone takes the plain build; Mac and iPad keep JSEP (47 runs in the
     *  same fortnight, none lost). Turn PHONE_JSEP back on only with a WebKit
     *  that fixed 304810, and watch `ort_jsep_phone_died` when you do.
     *
     *  CPU threads. A cross-origin-isolated page (coep_test.paid_tools —
     *  Premium, who see no ads) gets SharedArrayBuffer and so a thread pool:
     *  measured 25.09.2026 on this model at 1280×768, ratio 0.375, wasm EP,
     *  three alternating rounds × 30 frames: 1 thread 821/965/949 ms/frame,
     *  2 threads 467/503/411, 4 threads 312/471/345 — ≈2.6× (median of medians
     *  949 → 345). The pool is spawned from ORT's glue module, so that module
     *  is imported from a Blob URL: a pthread worker inherits the page's policy
     *  from a blob, while a static module lives or dies by the COEP header of
     *  whoever serves it (RIFE, 12.09: InferenceSession.create hung 150 s,
     *  silently, on a first visit without the Service Worker). And the create
     *  runs under a watchdog: a pool that does not come up in CREATE_DEADLINE_MS
     *  is abandoned for a FRESH runtime with one thread (a failed ORT init
     *  poisons its global for good), and the device is remembered for 30 days.
     *  Safari never reaches this lane: it does not implement
     *  `COEP: credentialless`, so an iPhone is never isolated. */
    var PHONE_KEY = 'rvm';
    var PHONE_JSEP = false;           // see the 02.10.2026 note above
    var SETTLE_FRAMES = 30;
    var CREATE_DEADLINE_MS = 45000;
    var MT_FAIL_KEY = 'rvm_mt_fail';
    var MT_FAIL_DAYS = 30;

    var ortOwned = false;             // this engine loaded window.ort itself
    var ortBuild = null;              // 'jsep' | 'plain' | 'legacy' | 'legacy-simd' | 'foreign'
    var ortThreads = 1;
    var mtNote = '';                  // why the pool was not used / was abandoned
    var noShare = false;              // this page could not reserve the threaded build's shared memory
    var noShareNote = '';
    // The owner (window.__ortBuild) keeps the same knowledge for every engine since
    // 2026-10-11: what another tool learned sends this one straight to 1.18, and
    // what this one learns is told back (rememberNoShare).
    function ownerNoShare() { try { var o = root.__ortBuild; return !!(o && o.noShare && o.noShare()); } catch (_) { return false; } }
    function legacyLane() { return !SIMD_OK || noShare || noShareRemembered() || ownerNoShare(); }
    function isLegacy() { return ortBuild === 'legacy' || ortBuild === 'legacy-simd'; }
    var phoneArmed = false;
    var framesSinceArm = 0;

    function webkit() {
        try { return !!(root.__deviceCap && root.__deviceCap.get().isWebKit); } catch (_) { return false; }
    }
    function isolated() { return typeof crossOriginIsolated !== 'undefined' && !!crossOriginIsolated; }
    function mtFailedRecently() {
        try {
            var t = +localStorage.getItem(MT_FAIL_KEY) || 0;
            return t > 0 && Date.now() - t < MT_FAIL_DAYS * 864e5;
        } catch (_) { return false; }
    }
    function rememberMtFail() { try { localStorage.setItem(MT_FAIL_KEY, String(Date.now())); } catch (_) {} }
    function threadBudget() {
        if (!SIMD_OK || legacyLane() || !isolated()) return 1;
        if (mtFailedRecently()) { mtNote = 'remembered'; return 1; }
        return Math.min(navigator.hardwareConcurrency || 4, 4);
    }
    function msgOf(e) { return String((e && e.message) || e).substring(0, 120); }
    function deadline(p, ms, what) {
        if (!ms) return p;
        return new Promise(function (resolve, reject) {
            var t = setTimeout(function () {
                reject(Object.assign(new Error(what + ' did not finish in ' + Math.round(ms / 1000) + ' s'), { code: 'deadline' }));
            }, ms);
            p.then(function (v) { clearTimeout(t); resolve(v); }, function (e) { clearTimeout(t); reject(e); });
        });
    }

    /* A real adapter, asked BEFORE the build is chosen: on WebKit an adapter-less
       JSEP build is the #26827 case, so «navigator.gpu exists» is not enough. */
    var adapterPromise = null;
    function hasAdapter() {
        if (adapterPromise) return adapterPromise;
        adapterPromise = (async function () {
            if (!SIMD_OK || !('gpu' in navigator) || !navigator.gpu) return false;
            try {
                var a = await deadline(navigator.gpu.requestAdapter(), 5000, 'requestAdapter');
                return !!a;
            } catch (_) { return false; }
        })();
        return adapterPromise;
    }

    /* The glue module from a Blob URL — see the note above. Returns false when
       the pool cannot be prepared (then the runtime is told to use one thread). */
    async function prepareThreads(base, plain) {
        var mjs = plain ? 'ort-wasm-simd-threaded.mjs' : 'ort-wasm-simd-threaded.jsep.mjs';
        var abs = new URL(base, root.location.href).href;
        try {
            var res = await deadline(fetch(abs + mjs, { credentials: 'same-origin' }), 20000, 'glue fetch');
            if (!res.ok) throw new Error('HTTP ' + res.status);
            var text = await res.text();
            var url = URL.createObjectURL(new Blob([text], { type: 'text/javascript' }));
            ort.env.wasm.wasmPaths = { mjs: url, wasm: abs + mjs.replace(/\.mjs$/, '.wasm') };
            return true;
        } catch (e) {
            mtNote = 'glue: ' + msgOf(e);
            return false;
        }
    }

    /* Load (or, with `fresh`, RE-load) the runtime this engine asked the owner
       for. A runtime someone else on the page already loaded is used as is:
       the build is the owner's answer for every consumer, and its env is not
       ours to reconfigure. */
    function loadOrt(opts, threads, fresh) {
        if (fresh) {
            if (!ortOwned) return Promise.reject(new Error('ONNX Runtime belongs to another engine on this page'));
            try { delete root.ort; } catch (_) { root.ort = undefined; }
            ortLoadingPromise = null;
        } else if (root.ort) {
            if (!ortBuild) { ortBuild = 'foreign'; try { ortThreads = ort.env.wasm.numThreads || 1; } catch (_) {} }
            return Promise.resolve();
        }
        if (ortLoadingPromise) return ortLoadingPromise;
        ortLoadingPromise = new Promise(function (resolve, reject) {
            function loadFrom(url, wasmBase, plain, onFail) {
                var s = document.createElement('script');
                // a re-load must evaluate the bundle again, not reuse the element
                s.src = fresh ? url + (url.indexOf('?') < 0 ? '?' : '&') + 'rvm_fresh=' + Date.now() : url;
                s.onload = async function () {
                    try {
                        if (root.ort && ort.env && ort.env.wasm) {
                            ort.env.wasm.wasmPaths = wasmBase;
                            if (isLegacy()) {
                                // 1.18 picks its file from these two: simd → ort-wasm-simd.wasm,
                                // without → ort-wasm.wasm; one thread → neither shares memory
                                ort.env.wasm.simd = SIMD_OK;
                                ort.env.wasm.numThreads = 1;
                                ortThreads = 1;
                            } else {
                                var n = threads > 1 && (await prepareThreads(wasmBase, plain)) ? threads : 1;
                                ort.env.wasm.numThreads = n;
                                ortThreads = n;
                            }
                        }
                    } catch (_) {}
                    ortOwned = true;
                    resolve();
                };
                s.onerror = onFail;
                document.head.appendChild(s);
            }
            if (legacyLane()) {
                ortBuild = SIMD_OK ? 'legacy-simd' : 'legacy';
                loadFrom(ORT_LEGACY, ORT_LEGACY_WASM_BASE, true, function () {
                    ortLoadingPromise = null;
                    reject(new Error('Failed to load ONNX Runtime (legacy)'));
                });
                return;
            }
            var ob = ortOwner();
            var primary = ob ? ob.url(opts) : ORT_FAILSAFE;
            var plain = /ort\.wasm\.min\.js/.test(primary);
            ortBuild = plain ? 'plain' : 'jsep';
            var base = (ob && typeof ob.wasmBase === 'function') ? ob.wasmBase() : ORT_WASM_BASE;
            var second = (ob && typeof ob.fallback === 'function') ? ob.fallback(opts) : null;
            loadFrom(primary, base, plain, function () {
                if (!second) { ortLoadingPromise = null; reject(new Error('Failed to load ONNX Runtime')); return; }
                // same-origin wasm under the twin, as before the owner existed
                loadFrom(second, base, plain, function () {
                    ortLoadingPromise = null;
                    reject(new Error('Failed to load ONNX Runtime'));
                });
            });
        });
        return ortLoadingPromise;
    }

    /** The SHARED resumable downloader (public/js/model-download.js).
     *
     *  The hand-rolled `reader.read()` loop that used to live here was the
     *  sixth copy that block exists to prevent, and rvm-engine.js sat in
     *  ModelDownloadResumeTest::LEGACY_SINGLE_PASS as a known gap. It cost a
     *  real report (25.08, iPhone/Yandex, 57 MB clip): a mobile radio that goes
     *  quiet mid-body leaves `read()` pending FOREVER — no error, no rejection —
     *  so ready() never settled, the bar sat at 12 % and the tool latched itself
     *  busy for the rest of the page load. `__mdFetchBytes` bounds SILENCE (not
     *  wall-clock: a slow-but-alive link keeps going), resumes by Range, and
     *  refuses to hand back a short body. */
    function loadDownloader() {
        if (root.__mdFetchBytes) return Promise.resolve(root.__mdFetchBytes);
        return new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            /* A page that knows the versioned URL wins; otherwise the house
               versioner. Unversioned code URLs are served immutable for six
               months on the intl origin. */
            s.src = root.__MODEL_DOWNLOAD_URL || (typeof root.__jsSrc === 'function' ? root.__jsSrc('/js/model-download.js') : '/js/model-download.js');
            s.onload = function () {
                root.__mdFetchBytes ? resolve(root.__mdFetchBytes)
                                    : reject(new Error('model-download loaded but __mdFetchBytes missing'));
            };
            s.onerror = function () { reject(new Error('model-download.js failed to load')); };
            document.head.appendChild(s);
        });
    }

    async function fetchModel(signal) {
        var mdFetchBytes = await loadDownloader();
        return await mdFetchBytes(MODEL_URL, {
            signal: signal,
            onProgress: function (received, total) {
                if (progressCb) { try { progressCb(received, total); } catch (_) {} }
            },
        });
    }

    /* One ORT run at a time on the PAGE. ORT-Web lets one run() be in flight
       per runtime, and an overlap kills both with «Session already started»;
       the stem separators share the same gate (window.__ortRunGate) for the same
       reason, in the same six lines — every one of these files is raw-loadable
       and none may depend on another's load order. 31 sessions met the overlap
       here (18.09–02.10) when the old frame source let two onFrame calls run
       at once; this keeps any later pair of callers from meeting it. */
    function ortRunSerialized(fn) {
        var gate = root.__ortRunGate || (root.__ortRunGate = { tail: Promise.resolve() });
        var out = gate.tail.then(fn, fn);
        gate.tail = out.then(function () {}, function () {});
        return out;
    }
    function isOverlap(e) { return /session already started/i.test(String((e && e.message) || e || '')); }

    /** A session is not trusted until it has actually RUN once.
     *
     *  ORT falls back when an execution provider is UNAVAILABLE, but not when
     *  the provider accepts the graph and then throws on an op it does not
     *  implement (the ceil_mode note in the header). One 64×64 forward is the
     *  cheapest possible proof, and it is the same call the tool makes a moment
     *  later — so what it proves is what the user actually gets. */
    async function verifySession(sess) {
        var names = sess.inputNames, feeds = {};
        var src = new ort.Tensor('float32', new Float32Array(3 * 64 * 64), [1, 3, 64, 64]);
        var ratio = new ort.Tensor('float32', new Float32Array([1]), [1]);
        var z = function () { return new ort.Tensor('float32', new Float32Array(1), [1, 1, 1, 1]); };
        if (names.indexOf('src') >= 0) {
            feeds.src = src;
            feeds.r1i = z(); feeds.r2i = z(); feeds.r3i = z(); feeds.r4i = z();
            feeds.downsample_ratio = ratio;
        } else {
            feeds[names[0]] = src;
            feeds[names[1]] = z(); feeds[names[2]] = z(); feeds[names[3]] = z(); feeds[names[4]] = z();
            feeds[names[5]] = ratio;
        }
        await ortRunSerialized(function () { return sess.run(feeds); });
    }

    /* One session, proven by one real forward pass (see verifySession). */
    async function createVerified(bytes, providers) {
        /* Pass the Uint8Array, not `.buffer`: the shared downloader may hand
           back a VIEW (single-chunk reads return the reader's own value), and
           `.buffer` would then be the whole underlying allocation. */
        // (a local named like the module's own: the verification rule is spelled
        // one way in every engine — OnnxWebgpuOpGapTest reads that spelling)
        var session = await ort.InferenceSession.create(bytes, {
            executionProviders: providers,
            graphOptimizationLevel: 'all',
        });
        try { await verifySession(session); }
        catch (e) { try { await session.release(); } catch (_) {} throw e; }
        return session;
    }

    /* The GPU lane failed (at create, at verify, or mid-run). On WebKit the
       step-down is the PLAIN build — never the wasm EP inside JSEP (#26827) —
       and the phone ledger remembers it; elsewhere the wasm EP of the same
       build, as before. */
    async function stepDownToCpu(why) {
        if (phoneArmed) {
            phoneArmed = false;
            var ob = ortOwner();
            try { ob && ob.phoneJsep && ob.phoneJsep.fail(PHONE_KEY, why); } catch (_) {}
        }
        if (session) { try { await session.release(); } catch (_) {} session = null; }
        if (webkit() && ortBuild === 'jsep' && ortOwned) {
            await loadOrt(undefined, 1, true);           // the owner's plain answer
        }
        var cpu = await ort.InferenceSession.create(modelBytes, {
            executionProviders: ['wasm'],
            graphOptimizationLevel: 'all',
        });
        await verifySession(cpu);                        // wasm proves itself too
        session = cpu;
        usedProvider = 'wasm';
        sessionGen++;
        ZERO = new ort.Tensor('float32', new Float32Array(1), [1, 1, 1, 1]);
    }

    function ensure(signal) {
        if (session) return Promise.resolve(true);
        if (loadingPromise) return loadingPromise;
        loadingPromise = (async function () {
            try {
                var gpu = await hasAdapter();
                var ob = ortOwner();
                var opts = gpu ? (PHONE_JSEP ? { jsepOnDesktopWebKit: true, jsepOnPhoneWebKit: PHONE_KEY } : { jsepOnDesktopWebKit: true }) : undefined;
                var threads = threadBudget();
                var wantJsep = SIMD_OK && gpu && !(ob && typeof ob.wasmOnly === 'function' && ob.wasmOnly(opts));
                if (wantJsep && !root.ort && ob && ob.phoneJsep) {
                    // no-op off an iPhone; must precede the build's first byte
                    ob.phoneJsep.arm(PHONE_KEY);
                    phoneArmed = true;
                    framesSinceArm = 0;
                }
                await loadOrt(opts, threads);
                var modelData = await fetchModel(signal);
                if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
                var providers = (wantJsep && ortBuild !== 'plain' && !isLegacy()) ? ['webgpu', 'wasm'] : ['wasm'];
                var used = providers[0], fellBack = '', mtFallback = '';
                var unlocked = unlockWebGpuOps(modelData);
                modelBytes = modelData;
                try {
                    session = await deadline(createVerified(modelData, providers),
                        ortThreads > 1 ? ((root.__RVM_CREATE_DEADLINE_MS | 0) || CREATE_DEADLINE_MS) : 0, 'session create');
                } catch (e1) {
                    // A pool that never came up (or died) looks like a hang or a
                    // worker error: a FRESH runtime with one thread, remembered.
                    var mtSuspect = ortThreads > 1 && ortOwned && (e1 && e1.code === 'deadline' || providers[0] === 'wasm');
                    if (mtSuspect) {
                        mtFallback = msgOf(e1);
                        await loadOrt(opts, 1, true);
                        try {
                            session = await createVerified(modelData, providers);
                            e1 = null;
                            rememberMtFail();        // one thread worked where the pool did not: that was the pool
                        } catch (e2) { e1 = e2; }
                    }
                    /* The modern build's shared memory could not even be
                       reserved (iOS ≤ 17, see ORT_LEGACY): 1.18 without shared
                       memory, in a FRESH runtime — a failed init is cached by the
                       one that failed — after a pause, because an immediate retry
                       meets the same exhausted address space. Remembered, so the
                       next visit goes straight there. */
                    if (!session && isReservationFailure(e1) && !isLegacy() && ortOwned) {
                        noShare = true; noShareNote = msgOf(e1);
                        rememberNoShare();
                        await new Promise(function (r) { setTimeout(r, 1500); });
                        await loadOrt(undefined, 1, true);
                        try {
                            session = await createVerified(modelData, ['wasm']);
                            e1 = null; used = 'wasm';
                        } catch (e3) { e1 = e3; }
                    }
                    if (!session) {
                        if (providers[0] !== 'webgpu' || isLegacy()) throw e1;      // nothing left below
                        fellBack = msgOf(e1);
                        await stepDownToCpu('create: ' + fellBack);
                        used = 'wasm';
                    }
                }
                ZERO = new ort.Tensor('float32', new Float32Array(1), [1, 1, 1, 1]);
                usedProvider = used;
                resetState();
                try {
                    window.__aeFeature && window.__aeFeature('rvm_model_loaded', {
                        sizeMB: Math.round(modelData.length / 1048576),
                        provider: usedProvider,
                        unlocked: unlocked,
                        fellback: fellBack ? 1 : 0,
                        fbmsg: fellBack || undefined,
                        legacy: SIMD_OK ? 0 : 1,
                        build: ortBuild,
                        threads: ortThreads,
                        coi: isolated() ? 1 : 0,
                        mtfb: mtFallback || undefined,
                        mtnote: mtNote || undefined,
                        noshare: noShare ? 1 : (noShareRemembered() ? 2 : undefined),
                        nsmsg: noShareNote || undefined,
                        phone: (PHONE_JSEP && ob && ob.phoneJsep && webkit()) ? ob.phoneJsep.verdict(PHONE_KEY) : undefined,
                    });
                } catch (_) {}
                return true;
            } catch (e) {
                lastError = e;
                session = null;
                loadingPromise = null;
                // Not the build's doing (network, abort, a verified CPU failure):
                // the mark must not outlive this attempt and read as a death.
                if (phoneArmed) {
                    phoneArmed = false;
                    try { var ob2 = ortOwner(); ob2 && ob2.phoneJsep && ob2.phoneJsep.settle(PHONE_KEY); } catch (_) {}
                }
                if (!(e && e.name === 'AbortError')) {
                    try { window.__aeFeature && window.__aeFeature('rvm_load_fail', { msg: String(e && e.message), build: ortBuild, threads: ortThreads }); } catch (_) {}
                }
                throw e;
            }
        })();
        return loadingPromise;
    }

    function resetState() {
        rState = ZERO ? [ZERO, ZERO, ZERO, ZERO] : null;
    }

    /** Downsample ratio — RVM runs its recurrent decoder at H·k × W·k. */
    function downsampleFor(w, h) {
        var long = Math.max(w, h);
        if (long >= 1920) return 0.25;
        if (long >= 1280) return 0.375;
        if (long >= 768)  return 0.5;
        return 1.0;
    }

    /** Canvas → CHW float32 RGB[0,1] tensor at (w,h) rounded to a multiple of 4. */
    function preprocess(srcCanvas) {
        /* Multiples of GRID (64 — see the note on GRID above), not of 4.
         *
         *  This is the PRECONDITION for unlockWebGpuOps() below: the three
         *  chained 2x2/stride-2 AveragePools only agree between ceil_mode 1 and
         *  0 when every intermediate feature map has even dimensions, and
         *  measurement says that holds exactly when both input dims divide by
         *  32. Feed 1080x1920 to the edited graph and it dies at a Concat with
         *  mismatched shapes; feed 1088x1920 and the output is bit-identical to
         *  the unedited model. The matte is scaled back to the source size
         *  afterwards, so rounding up costs nothing visible. */
        var w = Math.max(GRID, Math.round(srcCanvas.width / GRID) * GRID);
        var h = Math.max(GRID, Math.round(srcCanvas.height / GRID) * GRID);
        /* One canvas and one input buffer for the whole pass, not a new pair per
           frame: at 720p that was a 1280×704 canvas plus 10.8 MB of Float32 for
           the collector on EVERY frame, and WebKit charges canvas memory against
           a page budget of its own. Safe to reuse because run() is serial
           (engineSerial) and ORT copies its inputs when the run starts. */
        var b = prebuf;
        if (!b.canvas) { b.canvas = document.createElement('canvas'); b.ctx = null; }
        if (b.canvas.width !== w || b.canvas.height !== h) { b.canvas.width = w; b.canvas.height = h; b.ctx = null; }
        if (!b.ctx) b.ctx = b.canvas.getContext('2d', { willReadFrequently: true });
        var cx = b.ctx;
        cx.clearRect(0, 0, w, h);
        cx.drawImage(srcCanvas, 0, 0, w, h);
        var d = cx.getImageData(0, 0, w, h).data;
        var n = w * h;
        if (!b.buf || b.buf.length !== 3 * n) b.buf = new Float32Array(3 * n);
        var inp = b.buf;
        for (var i = 0, j = 0; i < d.length; i += 4, j++) {
            inp[j]         = d[i]     / 255;
            inp[n + j]     = d[i + 1] / 255;
            inp[2 * n + j] = d[i + 2] / 255;
        }
        return { data: inp, w: w, h: h };
    }
    var prebuf = { canvas: null, ctx: null, buf: null };

    /** Build the feeds object, mapping by name with positional fallback. */
    function buildFeeds(srcTensor, ratioTensor) {
        var names = session.inputNames;
        var feeds = {};
        var has = {};
        for (var i = 0; i < names.length; i++) has[names[i]] = true;
        if (has.src) {
            feeds.src = srcTensor;
            feeds.r1i = rState[0]; feeds.r2i = rState[1];
            feeds.r3i = rState[2]; feeds.r4i = rState[3];
            feeds.downsample_ratio = ratioTensor;
        } else {
            // Positional: [src, r1i, r2i, r3i, r4i, downsample_ratio]
            feeds[names[0]] = srcTensor;
            feeds[names[1]] = rState[0]; feeds[names[2]] = rState[1];
            feeds[names[3]] = rState[2]; feeds[names[4]] = rState[3];
            feeds[names[5]] = ratioTensor;
        }
        return feeds;
    }

    /** Pull fgr / pha / recurrent outputs from a run() result. */
    function readOutputs(out) {
        var fgr = out.fgr, pha = out.pha;
        var r = [out.r1o, out.r2o, out.r3o, out.r4o];
        if (!fgr || !pha) {
            // Positional fallback — outputs are [fgr, pha, r1o..r4o].
            var names = session.outputNames;
            fgr = out[names[0]]; pha = out[names[1]];
            r = [out[names[2]], out[names[3]], out[names[4]], out[names[5]]];
        }
        return { fgr: fgr, pha: pha, state: r };
    }

    /** fgr [1,3,H,W] + pha [1,1,H,W] → RGBA canvas at the source dimensions. */
    function buildMatteCanvas(fgr, pha, dstW, dstH) {
        var dims = pha.dims;            // [1,1,H,W]
        var h = dims[2], w = dims[3];
        var n = w * h;
        var f = fgr.data, p = pha.data;
        /* The canvas the caller gets is always a new one (callers keep it — a
           photo editor's cut-out is its result). The network-sized one in
           between is ours, and is reused whenever it is only a step. */
        var scaled = !(w === dstW && h === dstH);
        var small, sctx, img;
        if (scaled) {
            if (!post.canvas) { post.canvas = document.createElement('canvas'); post.ctx = null; }
            if (post.canvas.width !== w || post.canvas.height !== h) { post.canvas.width = w; post.canvas.height = h; post.ctx = null; post.img = null; }
            if (!post.ctx) post.ctx = post.canvas.getContext('2d');
            if (!post.img) post.img = post.ctx.createImageData(w, h);
            small = post.canvas; sctx = post.ctx; img = post.img;
        } else {
            small = document.createElement('canvas');
            small.width = w; small.height = h;
            sctx = small.getContext('2d');
            img = sctx.createImageData(w, h);
        }
        var d = img.data;
        for (var i = 0, j = 0; i < n; i++, j += 4) {
            d[j]     = Math.max(0, Math.min(255, f[i] * 255));
            d[j + 1] = Math.max(0, Math.min(255, f[n + i] * 255));
            d[j + 2] = Math.max(0, Math.min(255, f[2 * n + i] * 255));
            d[j + 3] = Math.max(0, Math.min(255, p[i] * 255));
        }
        sctx.putImageData(img, 0, 0);
        if (!scaled) return small;
        var out = document.createElement('canvas');
        out.width = dstW; out.height = dstH;
        out.getContext('2d').drawImage(small, 0, 0, dstW, dstH);
        return out;
    }
    var post = { canvas: null, ctx: null, img: null };

    /* run() — preprocess into the shared buffers, the network, the recurrent
       state — happens for ONE caller at a time on this engine: the buffers
       above are shared, and two interleaved calls would also step the
       recurrent state for each other. */
    var engineTail = Promise.resolve();
    function engineSerial(fn) {
        var out = engineTail.then(fn, fn);
        engineTail = out.then(function () {}, function () {});
        return out;
    }

    async function run(srcCanvas) {
        if (!session) throw new Error('RVM session not ready');
        var dstW = srcCanvas.width, dstH = srcCanvas.height;
        var pre = preprocess(srcCanvas);
        var srcTensor = new ort.Tensor('float32', pre.data, [1, 3, pre.h, pre.w]);
        var ratio = downsampleFor(pre.w, pre.h);
        var ratioTensor = new ort.Tensor('float32', new Float32Array([ratio]), [1]);
        var out, feeds = buildFeeds(srcTensor, ratioTensor), sess = session;
        try {
            out = await ortRunSerialized(function () { return sess.run(feeds); });
        } catch (e) {
            // The GPU lane can fail AFTER it proved itself — «[webgpu] Out of
            // memory» on a phone is the known one. Step down once and redo this
            // frame on the CPU; the recurrent state restarts (one frame of
            // re-convergence), which beats losing the whole pass.
            // An overlap of two runs is not the GPU's failure, and the CPU would
            // only meet the same overlap more slowly (until 02.10 it was read as
            // one: «Session already started» stepped 31 sessions down to wasm).
            if (usedProvider !== 'webgpu' || !modelBytes || isOverlap(e)) throw e;
            var why = msgOf(e);
            try { window.__aeFeature && window.__aeFeature('rvm_gpu_run_failed', { msg: why, build: ortBuild, frames: framesSinceArm }); } catch (_) {}
            await stepDownToCpu('run: ' + why);
            resetState();
            srcTensor = new ort.Tensor('float32', pre.data, [1, 3, pre.h, pre.w]);
            ratioTensor = new ort.Tensor('float32', new Float32Array([ratio]), [1]);
            var feeds2 = buildFeeds(srcTensor, ratioTensor), cpu = session;
            out = await ortRunSerialized(function () { return cpu.run(feeds2); });
        }
        var parsed = readOutputs(out);
        rState = parsed.state;          // carry recurrent state forward
        if (phoneArmed && ++framesSinceArm >= SETTLE_FRAMES) {
            phoneArmed = false;
            try { var ob = ortOwner(); ob && ob.phoneJsep && ob.phoneJsep.settle(PHONE_KEY); } catch (_) {}
        }
        return buildMatteCanvas(parsed.fgr, parsed.pha, dstW, dstH);
    }

    async function matte(srcCanvas) {
        await ensure();
        return engineSerial(function () { return run(srcCanvas); });
    }

    async function matteStateless(srcCanvas) {
        await ensure();
        // the state swap belongs inside the same turn as the run it brackets
        return engineSerial(async function () {
            var saved = rState, gen = sessionGen;
            resetState();
            try { return await run(srcCanvas); }
            finally {
                // a step-down in between rebuilt the runtime: the saved tensors belong
                // to the one that is gone
                if (gen === sessionGen) rState = saved; else resetState();
            }
        });
    }

    root.VbrRvmEngine = {
        ready: ensure,
        isReady: function () { return !!session; },
        getError: function () { return lastError; },
        provider: function () { return usedProvider; },
        resetState: resetState,
        downsampleFor: downsampleFor,
        matte: matte,
        matteStateless: matteStateless,
        onModelProgress: function (fn) { progressCb = fn; },
    };
})(typeof window !== 'undefined' ? window : globalThis);
